import React, { useEffect, useState, useCallback, useMemo } from "react";
import { MediaProvider } from "../../../contexts/MediaContext";
import MemoInput from "../../../components/MemoInput";
import { getCustomerUniqueKey } from "../../../utils/customerUtils";

function TrialListPageContent() {
  const [loadingText, setLoadingText] = useState("");
  const [trialCustomers, setTrialCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filterTrialDateStart, setFilterTrialDateStart] = useState("");
  const [filterTrialDateEnd, setFilterTrialDateEnd] = useState("");
  const [nameSortOrder, setNameSortOrder] = useState(""); // "asc", "desc", ""
  const [ageSortOrder, setAgeSortOrder] = useState(""); // "asc", "desc", ""
  const [filterStudio, setFilterStudio] = useState("");
  const [filterLesson, setFilterLesson] = useState("");
  const [lessonsList, setLessonsList] = useState([]);
  const [filterApplicationDate, setFilterApplicationDate] = useState("");
  const [selectedGenders, setSelectedGenders] = useState([]); // 複数選択の性別フィルター
  const [searchText, setSearchText] = useState(""); // 検索テキスト
  const [expandedGroupKeys, setExpandedGroupKeys] = useState({}); // 複数予約の開閉状態

  // 体験者一覧データを取得する関数
  const fetchTrialCustomersData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_trial_customers/?page_size=999999`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      const customers = data.trial_customers || [];
      setTrialCustomers(customers);
    } catch (error) {
      console.error("データ取得エラー:", error);
      // エラー時は空のデータを設定
      setTrialCustomers([]);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchTrialCustomersData();
  }, [fetchTrialCustomersData]);

  // 体験テーブルから店舗に紐づくレッスン一覧を取得
  const fetchLessons = useCallback(async (studioId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const params = studioId ? `?studio_id=${studioId}` : "";
      const response = await fetch(`${apiUrl}/api/get_trial_lessons_list/${params}`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      setLessonsList(data.lessons || []);
    } catch (error) {
      setLessonsList([]);
    }
  }, []);

  useEffect(() => {
    fetchLessons("");
  }, [fetchLessons]);

  useEffect(() => {
    fetchLessons(filterStudio);
    setFilterLesson("");
  }, [filterStudio, fetchLessons]);

  // 体験者一覧の再読み込み
  const handleReset = async () => {
    setFilterTrialDateStart("");
    setFilterTrialDateEnd("");
    setNameSortOrder("");
    setAgeSortOrder("");
    setFilterStudio("");
    setFilterLesson("");
    setFilterApplicationDate("");
    setSelectedGenders([]);
    setSearchText("");
    await fetchTrialCustomersData();
  };

  // 体験者の予約日を更新する関数
  const updateTrialCustomerDate = async (customerId, newDate) => {
    setLoadingText("更新中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_trial_customer/${customerId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            trial_date: newDate,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "更新に失敗しました");
      }

      const data = await response.json();
      // 全件再取得せず、該当行のみローカル更新
      setTrialCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId ? { ...c, trial_date: newDate } : c,
        ),
      );

      return data;
    } catch (error) {
      console.error("更新エラー:", error);
      throw error;
    } finally {
      setLoadingText("");
    }
  };

  // 体験者を削除する関数
  const deleteTrialCustomer = async (customerId) => {
    setLoadingText("削除中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/delete_trial_customer/${customerId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "削除に失敗しました");
      }

      const data = await response.json();
      // 全件再取得せず、該当行をローカルから除去
      setTrialCustomers((prev) => prev.filter((c) => c.id !== customerId));

      return data;
    } catch (error) {
      console.error("削除エラー:", error);
      throw error;
    } finally {
      setLoadingText("");
    }
  };

  // メモを更新する関数（MemoInputコンポーネント用）
  const updateMemo = useCallback(async (customerId, memo) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(
      `${apiUrl}/api/update_trial_customer/${customerId}/`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          memo: memo,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "メモの更新に失敗しました");
    }

    const data = await response.json();
    // 成功時、TrialCustomersのメモフィールドを更新
    setTrialCustomers((prev) =>
      prev.map((customer) =>
        customer.id === customerId ? { ...customer, memo: memo } : customer
      )
    );

    return data;
  }, []);

  // 体験済み状態を更新する関数
  const updateTrialStatus = async (customerId, isTrialed) => {
    setLoadingText("更新中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_trial_customer/${customerId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            is_trialed: isTrialed,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "更新に失敗しました");
      }

      const data = await response.json();
      // 全件再取得せず、該当行のみローカル更新
      setTrialCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId ? { ...c, is_trialed: isTrialed } : c,
        ),
      );

      return data;
    } catch (error) {
      console.error("更新エラー:", error);
      throw error;
    } finally {
      setLoadingText("");
    }
  };

  // 入会済み状態を更新する関数
  const updateMemberStatus = async (customerId, isMember, customerName) => {
    setLoadingText("更新中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_trial_customer/${customerId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            is_member: isMember,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "更新に失敗しました");
      }

      const data = await response.json();
      // 全件再取得せず、サーバーの連動条件(姓・名・メール・電話が全一致)に合わせて
      // 同一人物の is_member をローカル一括更新する
      setTrialCustomers((prev) => {
        const target = prev.find((c) => c.id === customerId);
        if (!target) return prev;
        return prev.map((c) =>
          c.last_name === target.last_name &&
          c.first_name === target.first_name &&
          c.mail === target.mail &&
          c.phone_number === target.phone_number
            ? { ...c, is_member: isMember }
            : c,
        );
      });

      return data;
    } catch (error) {
      console.error("更新エラー:", error);
      throw error;
    } finally {
      setLoadingText("");
    }
  };

  // 性別ボタンのクリック処理
  const handleGenderToggle = (gender) => {
    setSelectedGenders((prev) => {
      if (prev.includes(gender)) {
        // 既に選択されている場合は除外
        return prev.filter((g) => g !== gender);
      } else {
        // 選択されていない場合は追加
        return [...prev, gender];
      }
    });
  };

  const toggleGroupExpansion = (groupKey) => {
    setExpandedGroupKeys((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // ユニークなスタジオリストを取得
  const getUniqueStudios = () => {
    const studios = trialCustomers
      .map((customer) => ({
        id: customer.studio_id,
        name: customer.studio_name,
      }))
      .filter((studio) => studio.name) // 空の名前を除外
      .reduce((unique, studio) => {
        // 重複を除去
        if (!unique.find((item) => item.id === studio.id)) {
          unique.push(studio);
        }
        return unique;
      }, []);

    return studios.sort((a, b) => a.name.localeCompare(b.name, "ja"));
  };

  // IDを指定した桁数で0埋めする関数
  const formatIdWithZeroPadding = (id, digits = 4) => {
    return String(id).padStart(digits, "0");
  };

  // 日付をYYYY/MM/DD形式にフォーマットする関数
  const formatDateToSlash = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  // 今日の日付をYYYY-MM-DD形式で取得
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayString = getTodayString();

  // 体験者リストアイテムコンポーネント
  const TrialCustomerRow = ({
    trialCustomer,
    isToday = false,
    relatedTrials = [],
  }) => {
    const groupKey = getCustomerGroupKey(trialCustomer);
    const expansionKey = `${groupKey}-${trialCustomer.id}`;
    const otherTrials = relatedTrials.filter(
      (trial) => trial.id !== trialCustomer.id
    );
    const hasMultipleReservations = otherTrials.length > 0;
    const isExpanded = !!expandedGroupKeys[expansionKey];

    const renderRow = (customer, options = {}) => {
      const {
        key,
        isChild = false,
        showToggleButton = false,
        todayHighlight = false,
      } = options;

      const className = [
        todayHighlight ? "today" : "",
        isChild ? "child-reservation" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <ul key={key} className={className}>
          {/* ID */}
          <li
            className="id-cell"
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {showToggleButton && (
              <button
                type="button"
                className={`reservation-toggle-btn ${isExpanded ? "open" : ""}`}
                aria-label="複数予約の表示切替"
              >
                {isExpanded ? "▼" : "▶"}
              </button>
            )}
            {!isChild ? formatIdWithZeroPadding(customer.id) : ""}
          </li>
          {/* 予約日 */}
          <li>
            <input
              type="date"
              value={customer.trial_date}
              onChange={async (e) => {
                const newDate = e.target.value;
                if (
                  window.confirm(
                    `本当に日付を変更しますか？\n新しい日付: ${formatDateToSlash(
                      newDate
                    )}`
                  )
                ) {
                  try {
                    await updateTrialCustomerDate(customer.id, newDate);
                    alert(
                      `日付が変更されました: ${formatDateToSlash(newDate)}`
                    );
                  } catch (error) {
                    alert(`エラーが発生しました: ${error.message}`);
                    // エラー時は元の値に戻す
                    e.target.value = customer.trial_date;
                  }
                } else {
                  // キャンセル時は元の値に戻す
                  e.target.value = customer.trial_date;
                }
              }}
            />
          </li>
          {/* 名前 */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.last_name} {customer.first_name}
          </li>
          {/* 年齢 */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.age}
          </li>
          {/* 性別 */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.gender}
          </li>
          {/* 店舗 */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.studio_name}
          </li>
          {/* レッスン */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.lesson_name}
          </li>
          {/* 申込日 */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {formatDateToSlash(customer.created_at)}
          </li>
          {/* 来店のきっかけ */}
          <li
            onClick={() =>
              showToggleButton ? toggleGroupExpansion(expansionKey) : null
            }
          >
            {customer.visit_reason}
          </li>
          {/* メモ */}
          <li>
            <MemoInput
              customerId={customer.id}
              initialValue={customer.memo || ""}
              onSave={updateMemo}
            />
          </li>
          {/* 体験済み */}
          <li className="icon-area">
            <input
              type="checkbox"
              checked={customer.is_trialed || false}
              onChange={async (e) => {
                const isChecked = e.target.checked;
                try {
                  await updateTrialStatus(customer.id, isChecked);
                } catch (error) {
                  // エラー時は元の状態に戻す
                  e.target.checked = customer.is_trialed;
                }
              }}
            />
          </li>
          {/* 入会済み */}
          <li className="icon-area">
            <input
              type="checkbox"
              checked={customer.is_member || false}
              onChange={async (e) => {
                const isChecked = e.target.checked;
                const customerName = `${customer.last_name} ${customer.first_name}`;

                try {
                  await updateMemberStatus(
                    customer.id,
                    isChecked,
                    customerName
                  );
                } catch (error) {
                  // エラー時は元の状態に戻す
                  e.target.checked = customer.is_member;
                }
              }}
            />
          </li>
          {/* メール */}
          <li className="icon-area">
            <a href={`mailto:${customer.mail}`}>
              <i className="fa fa-envelope" aria-label="メール"></i>
            </a>
          </li>
          {/* 電話 */}
          <li className="icon-area">
            <a href={`tel:${customer.phone_number}`}>
              <i className="fa fa-phone" aria-label="電話"></i>
            </a>
          </li>
          <li className="icon-area">
            <i
              className="fa fa-minus"
              aria-label="削除"
              style={{ cursor: "pointer" }}
              onClick={async () => {
                const customerId = formatIdWithZeroPadding(customer.id);
                if (
                  window.confirm(
                    `本当に「${customerId}」削除しますか？\n※削除したデータは元に戻せません。`
                  )
                ) {
                  try {
                    await deleteTrialCustomer(customer.id);
                    alert(`ID: ${customerId} を削除しました。`);
                  } catch (error) {
                    alert(`削除に失敗しました: ${error.message}`);
                  }
                }
              }}
            ></i>
          </li>
        </ul>
      );
    };

    return (
      <>
        {renderRow(trialCustomer, {
          key: `main-${trialCustomer.id}`,
          showToggleButton: hasMultipleReservations,
          todayHighlight: isToday,
        })}
        {hasMultipleReservations && (
          <div
            className={`group-toggle-panel ${isExpanded ? "open" : ""}`}
            aria-hidden={!isExpanded}
          >
            {otherTrials.map((trial) =>
              renderRow(trial, {
                key: `child-${trial.id}`,
                isChild: true,
                todayHighlight: trial.trial_date === todayString,
              })
            )}
          </div>
        )}
      </>
    );
  };

  // フィルタリング・ソート処理
  const filteredCustomers = useMemo(() => {
    let filtered = trialCustomers;

    // 予約日でフィルタリング（期間指定）
    if (filterTrialDateStart && filterTrialDateEnd) {
      filtered = filtered.filter(
        (customer) =>
          customer.trial_date >= filterTrialDateStart &&
          customer.trial_date <= filterTrialDateEnd
      );
    } else if (filterTrialDateStart) {
      filtered = filtered.filter(
        (customer) => customer.trial_date >= filterTrialDateStart
      );
    } else if (filterTrialDateEnd) {
      filtered = filtered.filter(
        (customer) => customer.trial_date <= filterTrialDateEnd
      );
    }

    // スタジオでフィルタリング
    if (filterStudio) {
      filtered = filtered.filter(
        (customer) => customer.studio_id === parseInt(filterStudio)
      );
    }

    // レッスンでフィルタリング
    if (filterLesson) {
      filtered = filtered.filter(
        (customer) => customer.lesson_name === filterLesson
      );
    }

    // 申込日でフィルタリング
    if (filterApplicationDate) {
      filtered = filtered.filter((customer) => {
        if (customer.created_at) {
          const createdDate = new Date(customer.created_at)
            .toISOString()
            .split("T")[0];
          return createdDate === filterApplicationDate;
        }
        return false;
      });
    }

    // 性別でフィルタリング（複数選択）
    if (selectedGenders.length > 0) {
      filtered = filtered.filter((customer) =>
        selectedGenders.includes(customer.gender)
      );
    }

    // 検索テキストでフィルタリング（名前、メール、電話番号、メモの部分一致）
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((customer) => {
        const fullName =
          `${customer.last_name} ${customer.first_name}`.toLowerCase();
        const email = (customer.mail || "").toLowerCase();
        const phone = (customer.phone_number || "").toLowerCase();
        const memo = (customer.memo || "").toLowerCase();

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower) ||
          memo.includes(searchLower)
        );
      });
    }

    // 名前でソート
    if (nameSortOrder) {
      filtered = [...filtered].sort((a, b) => {
        const nameA = `${a.last_name} ${a.first_name}`;
        const nameB = `${b.last_name} ${b.first_name}`;

        if (nameSortOrder === "asc") {
          return nameA.localeCompare(nameB, "ja");
        } else if (nameSortOrder === "desc") {
          return nameB.localeCompare(nameA, "ja");
        }
        return 0;
      });
    }

    // 年齢でソート
    if (ageSortOrder) {
      filtered = [...filtered].sort((a, b) => {
        const ageA = parseInt(a.age) || 0;
        const ageB = parseInt(b.age) || 0;

        if (ageSortOrder === "asc") {
          return ageA - ageB;
        } else if (ageSortOrder === "desc") {
          return ageB - ageA;
        }
        return 0;
      });
    }

    return filtered;
  }, [
    trialCustomers,
    filterTrialDateStart,
    filterTrialDateEnd,
    filterStudio,
    filterLesson,
    filterApplicationDate,
    selectedGenders,
    searchText,
    nameSortOrder,
    ageSortOrder,
  ]);

  // 体験者データを名前でグループ化する関数（共有ユーティリティを使用）
  const getCustomerGroupKey = getCustomerUniqueKey;

  const groupCustomersByName = (customers) => {
    const groups = {};

    customers.forEach((customer) => {
      const groupKey = getCustomerGroupKey(customer);
      if (!groups[groupKey]) {
        groups[groupKey] = {
          customerInfo: {
            name: `${customer.last_name} ${customer.first_name}`,
            age: customer.age,
            gender: customer.gender,
            is_member: customer.is_member,
            mail: customer.mail,
            phone_number: customer.phone_number,
          },
          trials: [],
        };
      }
      groups[groupKey].trials.push(customer);
    });

    return Object.entries(groups).map(([groupKey, group]) => ({
      ...group,
      groupKey,
      trials: group.trials,
    }));
  };

  // 体験者を本日分とその他に分ける
  // filteredCustomers は上の useMemo で算出済み。グループ化のみ memo 化する。
  const groupedCustomers = useMemo(
    () => groupCustomersByName(filteredCustomers),
    [filteredCustomers],
  );
  const groupedCustomersMap = groupedCustomers.reduce((acc, group) => {
    acc[group.groupKey] = group.trials;
    return acc;
  }, {});

  const todayCustomers = filteredCustomers.filter(
    (customer) => customer.trial_date === todayString
  );
  const otherCustomers = filteredCustomers.filter(
    (customer) => customer.trial_date !== todayString
  );

  return (
    <>
      {/* メッセージ表示 */}
      {message && <div className="message success">{message}</div>}

      {/* 絞り込みエリア（横スクロール対応） */}
      <div className="filterSectionWrap">
        <div className="filterSectionInner">
          <div className="buttonWrap">
            <button
              className="filter"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              フィルター・絞り込み
            </button>
            <button className="sort-reset" onClick={handleReset}>
              リセット
            </button>
          </div>
          <div className={`sort-setting ${isFilterOpen ? "on" : ""}`}>
        <dl>
          <dt>予約日</dt>
          <dd>
            <input
              type="date"
              placeholder="開始日"
              value={filterTrialDateStart}
              onChange={(e) => setFilterTrialDateStart(e.target.value)}
            />
            <span>〜</span>
            <input
              type="date"
              placeholder="終了日"
              value={filterTrialDateEnd}
              onChange={(e) => setFilterTrialDateEnd(e.target.value)}
            />
          </dd>
        </dl>
        <dl>
          <dt>名前</dt>
          <dd>
            <button
              className={nameSortOrder === "asc" ? "on" : ""}
              onClick={() => {
                setNameSortOrder(nameSortOrder === "asc" ? "" : "asc");
                setAgeSortOrder("");
              }}
            >
              昇順
            </button>
            <button
              className={nameSortOrder === "desc" ? "on" : ""}
              onClick={() => {
                setNameSortOrder(nameSortOrder === "desc" ? "" : "desc");
                setAgeSortOrder("");
              }}
            >
              降順
            </button>
          </dd>
        </dl>
        <dl>
          <dt>年齢</dt>
          <dd>
            <button
              className={ageSortOrder === "asc" ? "on" : ""}
              onClick={() => {
                setAgeSortOrder(ageSortOrder === "asc" ? "" : "asc");
                setNameSortOrder("");
              }}
            >
              昇順
            </button>
            <button
              className={ageSortOrder === "desc" ? "on" : ""}
              onClick={() => {
                setAgeSortOrder(ageSortOrder === "desc" ? "" : "desc");
                setNameSortOrder("");
              }}
            >
              降順
            </button>
          </dd>
        </dl>
        <dl>
          <dt>店舗</dt>
          <dd>
            <select
              value={filterStudio}
              onChange={(e) => setFilterStudio(e.target.value)}
            >
              <option value="">すべて</option>
              {getUniqueStudios().map((studio) => (
                <option key={studio.id} value={studio.id}>
                  {studio.name}
                </option>
              ))}
            </select>
          </dd>
        </dl>
        <dl>
          <dt>レッスン</dt>
          <dd>
            <select
              value={filterLesson}
              onChange={(e) => setFilterLesson(e.target.value)}
            >
              <option value="">すべて</option>
              {[
                "monday", "tuesday", "wednesday", "thursday",
                "friday", "saturday", "sunday",
              ].map((day) => {
                const dayLessons = lessonsList.filter((l) => l.day === day);
                if (dayLessons.length === 0) return null;
                const dayJa = dayLessons[0].day_ja;
                return (
                  <optgroup key={day} label={`${dayJa}曜日`}>
                    {dayLessons.map((lesson) => (
                      <option key={`${lesson.studio_id}-${lesson.name}`} value={lesson.name}>
                        {!filterStudio && lesson.studio_name
                          ? `${lesson.studio_name} - ${lesson.name}`
                          : lesson.name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </dd>
        </dl>
        <dl>
          <dt>申込日</dt>
          <dd>
            <input
              type="date"
              value={filterApplicationDate}
              onChange={(e) => setFilterApplicationDate(e.target.value)}
            />
          </dd>
        </dl>
        <dl>
          <dt>性別</dt>
          <dd>
            <button
              className={selectedGenders.includes("男性") ? "on" : ""}
              onClick={() => handleGenderToggle("男性")}
            >
              男性
            </button>
            <button
              className={selectedGenders.includes("女性") ? "on" : ""}
              onClick={() => handleGenderToggle("女性")}
            >
              女性
            </button>
            <button
              className={selectedGenders.includes("その他") ? "on" : ""}
              onClick={() => handleGenderToggle("その他")}
            >
              その他
            </button>
          </dd>
        </dl>
        <dl className="saerch">
          <dt>
            <i className="fa-thin fa-magnifying-glass"></i>
          </dt>
          <dd>
            <input
              type="text"
              placeholder="名前 / メールアドレス / 電話番号 / メモで検索"
              value={searchText || ""}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </dd>
        </dl>
          </div>
        </div>
      </div>

      {/* 名前でソートされている場合のみcategory-tableを表示 */}
      {nameSortOrder && (
        <div className="category-table">
          <table>
            {/* ラベル */}
            <thead>
              <tr>
                <th>体験数</th>
                <th>予約日</th>
                <th>店舗</th>
                <th>レッスン</th>
                <th>申込日</th>
                <th>来店のきっかけ</th>
                <th>メモ</th>
                <th>削除</th>
              </tr>
            </thead>

            <tbody>
              {groupedCustomers.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {/* 親 */}
                  <tr className="parent">
                    <td colSpan={8}>
                      <div>
                        <p>
                          {group.customerInfo.name}　({group.customerInfo.age})
                        </p>
                        <p>{group.customerInfo.gender}</p>
                        <p>
                          入会
                          <input
                            type="checkbox"
                            checked={group.customerInfo.is_member || false}
                            onChange={async (e) => {
                              const isChecked = e.target.checked;
                              try {
                                await updateMemberStatus(
                                  group.trials[0].id,
                                  isChecked,
                                  group.customerInfo.name
                                );
                              } catch (error) {
                                e.target.checked = group.customerInfo.is_member;
                              }
                            }}
                          />
                        </p>
                        <p>
                          <a href={`mailto:${group.customerInfo.mail}`}>
                            <i
                              className="fa fa-envelope"
                              aria-label="メール"
                            ></i>
                          </a>
                        </p>
                        <p>
                          <a href={`tel:${group.customerInfo.phone_number}`}>
                            <i className="fa fa-phone" aria-label="電話"></i>
                          </a>
                        </p>
                      </div>
                    </td>
                  </tr>
                  {/* 子 */}
                  {group.trials &&
                    group.trials.map((trial, trialIndex) => (
                      <tr key={trial.id} className="child">
                        <td>
                          <span>{group.trials.length - trialIndex}回目</span>
                        </td>
                        <td>{formatDateToSlash(trial.trial_date)}</td>
                        <td>{trial.studio_name}</td>
                        <td>{trial.lesson_name}</td>
                        <td>{formatDateToSlash(trial.created_at)}</td>
                        <td>{trial.visit_reason}</td>
                        <td>
                          <MemoInput
                            customerId={trial.id}
                            initialValue={trial.memo || ""}
                            onSave={updateMemo}
                          />
                        </td>
                        <td>
                          <i
                            className="fa fa-minus"
                            aria-label="削除"
                            style={{ cursor: "pointer" }}
                            onClick={async () => {
                              const customerId = formatIdWithZeroPadding(
                                trial.id
                              );
                              if (
                                window.confirm(
                                  `本当に「${customerId}」削除しますか？\n※削除したデータは元に戻せません。`
                                )
                              ) {
                                try {
                                  await deleteTrialCustomer(trial.id);
                                  alert(`ID: ${customerId} を削除しました。`);
                                } catch (error) {
                                  alert(`削除に失敗しました: ${error.message}`);
                                }
                              }
                            }}
                          ></i>
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 名前でソートされていない場合のみdataBoxWrapを表示 */}
      {!nameSortOrder && (
        <div className="dataBoxWrap">
          <div className="dataBox">
            <ul>
              <li className="ttl sort-on">
                <p>ID</p>
              </li>
              <li className="ttl">
                <p>予約日</p>
              </li>
              <li className="ttl">
                <p>名前</p>
              </li>
              <li className="ttl">
                <p>年齢</p>
              </li>
              <li className="ttl">
                <p>性別</p>
              </li>
              <li className="ttl">
                <p>店舗</p>
              </li>
              <li className="ttl">
                <p>レッスン</p>
              </li>
              <li className="ttl">
                <p>申込日</p>
              </li>
              <li className="ttl">
                <p>来店のきっかけ</p>
              </li>
              <li className="ttl">
                <p>メモ</p>
              </li>
              <li className="ttl">
                <p>体験済み</p>
              </li>
              <li className="ttl">
                <p>入会済み</p>
              </li>
              <li className="ttl">
                <p>メール</p>
              </li>
              <li className="ttl">
                <p>電話</p>
              </li>
              <li className="ttl">
                <p>削除</p>
              </li>
            </ul>

            {/* 本日分 */}
            {todayCustomers.length > 0 &&
              todayCustomers.map((trialCustomer) => (
                <TrialCustomerRow
                  key={`today-${trialCustomer.id}`}
                  trialCustomer={trialCustomer}
                  isToday={true}
                  relatedTrials={
                    groupedCustomersMap[getCustomerGroupKey(trialCustomer)] ||
                    []
                  }
                />
              ))}

            {/* その他の体験予定者 */}
            {otherCustomers.length > 0 &&
              otherCustomers.map((trialCustomer) => (
                <TrialCustomerRow
                  key={`other-${trialCustomer.id}`}
                  trialCustomer={trialCustomer}
                  relatedTrials={
                    groupedCustomersMap[getCustomerGroupKey(trialCustomer)] ||
                    []
                  }
                />
              ))}
          </div>
        </div>
      )}

      {/* ローディング表示 */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
    </>
  );
}

export default function TrialListPage() {
  return (
    <MediaProvider>
      <TrialListPageContent />
    </MediaProvider>
  );
}
